import React from "react";
import { Card } from "reactstrap";
import moment from "moment";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

import Grid from "../../Utils/Grid";
import Map from "../../Utils/Map";

const CardContainer = ({
    dashboardData,
    loader,
    start_date,
    end_date,
    permissions = false,
    isDashboardOpen = false,
}) => {
    const columns = [
        {
            dataField: "id",
            text: "Codigo",
        },
        {
            dataField: "attention_date",
            text: "Fecha",
            formatter: (cell, row) => (
                <div>{moment(row.attention_date).format("MM/DD/YYYY")}</div>
            ),
        },
        {
            dataField: "hour_creation_data",
            text: "Hora",
            formatter: (cell, row) => (
                <div>{moment(row.creation_date).format(" HH:SS ")}</div>
            ),
        },
        {
            dataField: "emergency_type_text",
            text: "Tipo",
            formatter: (cell, row) => <div>{row.emergency_type_text ? row.emergency_type_text : 'Sin tipo'}</div>,
        },
        {
            dataField: "address",
            text: "Dirección",
            formatter: (cell, row) => <div>{row.address}</div>,
        },
        {
            dataField: "station",
            text: "Estación",
            formatter: (cell, row) => (
                <div>
                    {row.stations.map(
                        (station) => station.station__name + ", "
                    )}
                </div>
            ),
        },
        {
            dataField: "status_in_text",
            text: "Estado",
            formatter: (cell, row) => {
                // Mapeo de colores según el estado
                const getStatusColor = (status) => {
                    const statusLower = status ? status.toLowerCase() : '';

                    if (statusLower.includes('activ') || statusLower.includes('en curso') || statusLower.includes('atendiendo')) {
                        return 'success'; // Verde
                    } else if (statusLower.includes('finaliz') || statusLower.includes('completad') || statusLower.includes('cerrad')) {
                        return 'secondary'; // Gris
                    } else if (statusLower.includes('pendiente') || statusLower.includes('espera')) {
                        return 'warning'; // Amarillo
                    } else if (statusLower.includes('cancelad') || statusLower.includes('rechazad')) {
                        return 'danger'; // Rojo
                    } else if (statusLower.includes('camino') || statusLower.includes('ruta')) {
                        return 'info'; // Azul
                    } else {
                        return 'primary'; // Azul por defecto
                    }
                };

                return (
                    <span className={`badge badge-pill badge-${getStatusColor(row.status_in_text)} px-3 py-2`}>
                        {row.status_in_text}
                    </span>
                );
            },
        },
    ];
    const COLORS = ["#004ccb", "#00c96f", "#ffb500", "#ff6300", "#6ac400"];

    const sanitizedChartData = (dashboardData.data || []).map(d => ({
        ...d,
        total: Math.max(0, parseFloat(d.total) || 0)
    }));

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className="custom-tooltip p-2"
                    style={{ backgroundColor: "white" }}
                >
                    <p className="intro">Tipo: {payload[0].payload.name}</p>
                    <p className="label">Total: {`${payload[0].value}`}</p>
                </div>
            );
        }

        return null;
    };

    const CustomLegend = () => {
        return (
            <div className="d-flex mt-2">
                {dashboardData.data.map((val, index) => {
                    return (
                        <div className="flex-1" key={index}>
                            <svg width="16" height="16" viewBox="0 0 32 32">
                                <path
                                    fill={COLORS[index % COLORS.length]}
                                    cx="16"
                                    cy="16"
                                    type="circle"
                                    transform="translate(16, 16)"
                                    d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
                                ></path>
                            </svg>
                            <span className="ml-2">{val ? val.name : 'Sin Datos'}</span>
                        </div>
                    );
                })}
            </div>
        );
    };
    return (
        <React.Fragment>
            {/* ROW 1: Table */}
            <div className="content-table-row">
                <Card className="m-2">
                    <h4 className="text-primary font-weight-bold">
                        Últimas 10 emergencias.
                    </h4>
                    <div className="overflow-auto">
                        <Grid
                            showPagination={false}
                            data={dashboardData}
                            loading={loader}
                            columns={columns}
                            noDataMessage={"Sin tipos de emergencia"}
                        />
                    </div>
                </Card>
            </div>

            {/* ROW 2: Maps */}
            <div className="content-bottom-grid">
                {permissions && !isDashboardOpen && (
                    <Card className="p-2">
                        <h6 className="card-title text-primary">
                            Vehículos Activos
                        </h6>
                        {dashboardData.vehicles && dashboardData.vehicles.length > 0 ? (
                            <React.Fragment>
                                <Map
                                    mapContainerStyle={{
                                        width: "100%",
                                        height: "400px",
                                    }}
                                    fromLat={dashboardData.vehicles.reduce((t, { latitude: l }) => {
                                        const val = parseFloat(l);
                                        return isFinite(val) && val > t ? val : t;
                                    }, 0)}
                                    fromLng={dashboardData.vehicles.reduce((t, { longitude: l }) => {
                                        const val = parseFloat(l);
                                        return isFinite(val) && val < t ? val : t;
                                    }, 0)}
                                    toLat={dashboardData.vehicles.reduce((t, { latitude: l }) => {
                                        const val = parseFloat(l);
                                        return isFinite(val) && val < t ? val : t;
                                    }, parseFloat(dashboardData.vehicles[0].latitude) || 0)}
                                    toLng={dashboardData.vehicles.reduce((t, { longitude: l }) => {
                                        const val = parseFloat(l);
                                        return isFinite(val) && val > t ? val : t;
                                    }, parseFloat(dashboardData.vehicles[0].longitude) || 0)}
                                    options={{
                                        fullscreenControl: false,
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                    }}

                                    trackingMap={dashboardData.vehicles.reduce(
                                        (arr, current, index) => {
                                            arr.push({
                                                coords: {
                                                    lat: parseFloat(current.latitude),
                                                    lng: parseFloat(current.longitude),
                                                },
                                                plate: `${current.plate}`,
                                                emergency_code: `${current.emergency_code}`,
                                                emergency_type: `${current.emergency_type}`,
                                                emergency_status: `${current.emergency_status}`,
                                                pilot: `${current.pilot}`,
                                                station: current.station,
                                                lt: current.latitude,
                                                lng: current.longitude,
                                            });
                                            return arr;
                                        },
                                        []
                                    )}
                                />
                            </React.Fragment>
                        ) : (
                            <div className="w-100 d-flex">
                                <h3 className="mx-auto py-5">
                                    {" "}
                                    No hay vehículos activos
                                </h3>
                            </div>
                        )}
                    </Card>
                )}

                <Card className="p-2">
                    <h6 className="card-title text-primary">Emergencias</h6>
                    {dashboardData.results && dashboardData.results.length > 0 ? (
                        <React.Fragment>
                            <Map
                                mapContainerStyle={{
                                    width: "100%",
                                    height: "400px",
                                }}
                                fromLat={dashboardData.results.reduce((t, { latitude: l }) => {
                                    const val = parseFloat(l);
                                    return isFinite(val) && val > t ? val : t;
                                }, 0)}
                                fromLng={dashboardData.results.reduce((t, { longitude: l }) => {
                                    const val = parseFloat(l);
                                    return isFinite(val) && val < t ? val : t;
                                }, 0)}
                                toLat={dashboardData.results.reduce((t, { latitude: l }) => {
                                    const val = parseFloat(l);
                                    return isFinite(val) && val < t ? val : t;
                                }, parseFloat(dashboardData.results[0].latitude) || 0)}
                                toLng={dashboardData.results.reduce((t, { longitude: l }) => {
                                    const val = parseFloat(l);
                                    return isFinite(val) && val > t ? val : t;
                                }, parseFloat(dashboardData.results[0].longitude) || 0)}
                                options={{
                                    fullscreenControl: false,
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                }}
                                emergencies={dashboardData.results.reduce(
                                    (arr, current, index) => {
                                        arr.push({
                                            coords: {
                                                lat: parseFloat(current.latitude),
                                                lng: parseFloat(current.longitude),
                                            },
                                            label: `${current.id}`,
                                        });
                                        return arr;
                                    },
                                    []
                                )}
                            />
                        </React.Fragment>
                    ) : (
                        <div className="w-100 d-flex">
                            <h3 className="mx-auto py-5">
                                {" "}
                                No hay emergencias registradas{" "}
                            </h3>
                        </div>
                    )}
                </Card>
            </div>

            {/* ROW 3: Charts & Stats */}
            <div className="content-bottom-row-2">
                <Card
                    className="m-2 overflow-card-x"
                    style={{ maxHeight: 500 }}
                >
                    <h4 className="text-primary font-weight-bold">
                        Top 5 tipos de emergencia
                    </h4>
                    {!isDashboardOpen && (
                        <div>
                            {start_date && end_date ? (
                                <React.Fragment>
                                    <span>
                                        {start_date
                                            ? moment(start_date).format(
                                                "DD/MM/YYYY"
                                            )
                                            : "(dd/mm/aa)"}
                                    </span>
                                    {" al "}
                                    <span>
                                        {end_date
                                            ? moment(end_date).format("DD/MM/YYYY")
                                            : "(dd/mm/aa)"}
                                    </span>
                                </React.Fragment>
                            ) : (
                                <span className="text-grey-subtittle">
                                    (dd/mm/aa) al (dd/mm/aa)
                                </span>
                            )}
                        </div>
                    )}
                    <div style={{ width: '100%', height: 350 }}>
                        <ResponsiveContainer>
                            <BarChart
                                data={sanitizedChartData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <Legend content={<CustomLegend />} />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="total" fill="#8884d8">
                                    {sanitizedChartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card>
                    <div className="p-4">
                        <h5 className="text-primary font-weight-bold mb-4">
                            Heridos registrados
                        </h5>

                        <div className="d-flex align-items-center mb-3 border-bottom pb-3">
                            <h2 className="font-weight-bold mb-0 mr-3" style={{ minWidth: '50px' }}>
                                {dashboardData.firearm ? dashboardData.firearm : 0}
                            </h2>
                            <span className="text-muted">Por Arma De Fuego</span>
                        </div>

                        <div className="d-flex align-items-center mb-3 border-bottom pb-3">
                            <h2 className="font-weight-bold mb-0 mr-3" style={{ minWidth: '50px' }}>
                                {dashboardData.white_weapon
                                    ? dashboardData.white_weapon
                                    : 0}
                            </h2>
                            <span className="text-muted">Por Arma Blanca</span>
                        </div>

                        <div className="d-flex align-items-center">
                            <h2 className="font-weight-bold mb-0 mr-3" style={{ minWidth: '50px' }}>
                                {dashboardData.wounded ? dashboardData.wounded : 0}
                            </h2>
                            <span className="text-muted">Heridos Varios</span>
                        </div>
                    </div>
                </Card>
            </div>
        </React.Fragment>
    );
};

export default CardContainer;
